import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  inject,
  Input,
  Output,
  QueryList,
} from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { TabComponent } from '../tab/tab.component';
import { CommonModule } from '@angular/common';
import { AppModuleCode } from 'src/core/interfaces/modules.interfaces';
import { PermissionsService } from 'src/core/services/permissions-service/permissions.service';

@Component({
  selector: 'tabs',
  imports: [CommonModule],
  templateUrl: './tabs.component.html',
  standalone: true,
})
export class TabsComponent implements AfterContentInit {
  private permissionsService = inject(PermissionsService);
  @ContentChildren(TabComponent) tabs!: QueryList<TabComponent>;
  @Input() routeParam: string = 'tab';
  @Input() enableRouting: boolean = false;
  @Input() defaultTab?: string = '';
  @Output() tabChanged = new EventEmitter<{
    index: number;
    label: string;
    name: string;
    module?: AppModuleCode;
  }>();

  constructor(private router: Router, private route: ActivatedRoute) {}

  get activeTabIndex(): number {
    return this.tabs?.toArray().findIndex((tab) => tab.active) ?? -1;
  }

  ngAfterContentInit() {
    if (this.enableRouting) {
      this.initializeFromRoute();
    } else {
      this.initializeDefaultTab();
    }
  }

  private initializeFromRoute() {
  this.route.paramMap.subscribe(params => {
    const routeParam = params.get(this.routeParam);

    if (routeParam) {
      const tabIndex = this.tabs
        .toArray()
        .findIndex((tab) => tab.name === routeParam);

      if (tabIndex !== -1) {
        this.selectTab(tabIndex, false);
        return;
      }
    }

    this.initializeDefaultTab();
  });
}

  private initializeDefaultTab() {
    const activeTabs = this.tabs.filter((tab) => tab.active);

    if (activeTabs.length > 0) {
      return;
    }
    
    if (this.defaultTab) {
      const defaultTabIndex = this.tabs.toArray().findIndex((tab) => tab.name === this.defaultTab);

      if (defaultTabIndex !== -1) {
        this.selectTab(defaultTabIndex, this.enableRouting);
        return;
      }
    }

    if (this.tabs.first) {
      this.selectTab(0, this.enableRouting);
    }
  }

  async selectTab(index: number, navigate: boolean = true) {
  this.tabs.forEach((tab, i) => (tab.active = i === index));

  const selectedTab = this.tabs.toArray()[index];
  if (selectedTab) {
    this.tabChanged.emit({
      index,
      label: selectedTab.label,
      name: selectedTab.name,
      module: selectedTab.module
    });

    if (this.enableRouting && navigate) {
      let canView = true;
      if(selectedTab.module !== undefined){
        canView = await this.permissionsService.canView(selectedTab.module, false, '/home');
      }
      if (canView) {
        this.router.navigate([selectedTab.name], { relativeTo: this.route.parent });
      }
    }
  }
}
}
